import { useQuery } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';

export type ApplicationExportSearchQuery = {
  'page[size]': number;
  'page[number]': number;
};

export type ApplicationExport = {
  id: string;
  fileName: string;
  scheduleName: string;
  fileUrl: string;
  dateExported: string;
  totalRecords: string;
  fileSize: string;
};

export type ListApplicationExportsResponse = {
  data: Array<{
    type: 'applicationExport';
    attributes: {
      fileName: string;
      scheduleName: string;
      fileUrl: string;
      dateExported: string;
      totalRecords: string;
      fileSize: string;
    };
  }>;
  meta?: {
    pagination?: {
      total: number;
      pageNumber: number;
      pageSize: number;
    };
  };
};

const defaultValue: ApplicationExport[] = [];

export function useApplicationExports(
  query: ApplicationExportSearchQuery,
  options?: {
    onSuccess?: (data: ApplicationExport[], total: number) => void;
    keepPreviousData?: boolean;
    enabled?: boolean;
  }
) {
  const request = useRequest();

  const { isLoading, isFetching, error, data, isPreviousData } = useQuery(
    ['applicationExports', query],
    async () => {
      const response = await request<ListApplicationExportsResponse>(
        `${config.apiHost}/private/v1/application_export?${convertToUrlSearchParams(query)}`,
        { method: 'GET', isExpectedResponse }
      );
      const applicationExports = response.data.map((file, index) => ({
        id: `${file.attributes.fileName}_${file.attributes.dateExported}_${index}`,
        fileName: file.attributes.fileName,
        scheduleName: file.attributes.scheduleName,
        fileUrl: file.attributes.fileUrl,
        dateExported: file.attributes.dateExported,
        totalRecords: file.attributes.totalRecords,
        fileSize: file.attributes.fileSize,
      }));
      const total = response.meta?.pagination?.total ?? 0;
      const pageNumber = response.meta?.pagination?.pageNumber ?? 1;
      const pageSize = response.meta?.pagination?.pageSize ?? 20;

      options?.onSuccess?.(applicationExports, total);
      return {
        pagination: {
          pageNumber: pageNumber,
          pageSize: pageSize,
        },
        applicationExports,
        totalApplicationExports: total,
      };
    },
    {
      enabled: options?.enabled === undefined ? true : options.enabled,
      keepPreviousData: options?.keepPreviousData === undefined ? true : options?.keepPreviousData,
    }
  );

  return {
    isPreviousApplicationExportsData: isPreviousData,
    applicationExportsPagination: data?.pagination,
    applicationExports: data?.applicationExports || defaultValue,
    applicationExportsError: error,
    isFetchingApplicationExports: isFetching,
    isLoadingApplicationExports: isLoading,
    totalApplicationExports: data?.totalApplicationExports ?? 0,
  };
}

const convertToUrlSearchParams = (params: ApplicationExportSearchQuery) => {
  return new URLSearchParams({
    'page[size]': `${params['page[size]']}`,
    'page[number]': `${params['page[number]']}`,
  });
};

function isExpectedResponse(res: unknown): res is ListApplicationExportsResponse {
  return Boolean(
    res && typeof res === 'object' && 'data' in res && Array.isArray((res as ListApplicationExportsResponse).data)
  );
}
